import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import { achievements } from '@/mocks/home';
import { fetchAchievementInfo, fetchAchievementList } from '@/services/achievement';
import { adaptAchievement, adaptAchievements } from '@/services/adapters';
import { resolveHtmlAssetUrls } from '@/services/request';
import { useRequest } from '@/hooks/useRequest';
import type { AchievementItem } from '@/types/content';

export default function AchievementDetailPage() {
  const { id } = useParams();
  const achievementId = Number(id);

  // 详情接口失败时降级为 mock 数据
  const { data: item, loading } = useRequest<AchievementItem | undefined>(
    () => fetchAchievementInfo(achievementId).then((res) => adaptAchievement(res.dataInfo)),
    { fallback: achievements.find((entry) => entry.id === achievementId), deps: [achievementId] },
  );

  // 更多成果列表（详情接口不返回，取列表接口排除当前成果）
  const { data: achievementList } = useRequest<AchievementItem[]>(
    () => fetchAchievementList({ pageIndex: 1, pageSize: 999 }).then((res) => adaptAchievements(res.list)),
    { fallback: achievements },
  );

  if (loading && !item) {
    return (
      <PageShell>
        <PageHero
          eyebrow="成果转化"
          title="正在加载成果信息"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '成果转化', to: '/achievements' }, { label: '成果详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-24 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </section>
      </PageShell>
    );
  }

  if (!item) {
    return (
      <PageShell>
        <PageHero
          eyebrow="成果转化"
          title="成果信息不存在"
          description="抱歉，未找到该成果信息，可能链接有误。"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '成果转化', to: '/achievements' }, { label: '成果详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-16 text-center">
          <Link
            to="/achievements"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回成果转化
          </Link>
        </section>
      </PageShell>
    );
  }

  const related = achievementList.filter((entry) => entry.id !== item.id).slice(0, 3);

  return (
    <PageShell>
      <PageHero
        eyebrow="成果转化"
        title={item.title}
        breadcrumb={[{ label: '首页', to: '/' }, { label: '成果转化', to: '/achievements' }, { label: '成果详情' }]}
      />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <span className="inline-flex items-center gap-1.5 text-xs text-primary-600 font-medium">
            <i className="ri-calendar-line"></i>
            发布时间：{item.date}
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-snug">
            {item.title}
          </h2>

          <div className="mt-6 w-full h-[260px] md:h-[420px] rounded-lg overflow-hidden border border-background-200">
            <img
              src={item.image}
              alt={item.title}
              title={`${item.title} - 成果转化`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8 flex items-start gap-3 p-5 rounded-lg bg-secondary-50 border border-secondary-100">
            <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-lightbulb-line text-lg"></i>
            </span>
            <div>
              <p className="text-sm font-medium text-secondary-900 mb-1">成果摘要</p>
              <p className="text-sm text-foreground-700 leading-relaxed">{item.summary}</p>
            </div>
          </div>

          <div className="mt-10">
            <h3 className="flex items-center gap-2 font-heading text-lg md:text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              成果亮点
            </h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {item.highlights.map((point, index) => (
                <div
                  key={index}
                  className="p-5 rounded-lg bg-background-100 border border-background-200"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 mb-3">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                  </span>
                  <p className="text-sm text-foreground-800 leading-relaxed">{point}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10">
            <h3 className="flex items-center gap-2 font-heading text-lg md:text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              成果详情
            </h3>
            <div className="mt-4 space-y-4">
              {item.detail.map((paragraph, index) =>
                paragraph.includes('<') ? (
                  <div
                    key={index}
                    className="rich-text"
                    dangerouslySetInnerHTML={{ __html: resolveHtmlAssetUrls(paragraph) }}
                  />
                ) : (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {paragraph}
                  </p>
                ),
              )}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto mt-14 md:mt-20">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">更多成果</h2>
          <div className="flex flex-col gap-4">
            {related.map((entry) => (
              <Link
                key={entry.id}
                to={`/achievements/${entry.id}`}
                className="group flex items-center gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-24 h-20 rounded-md overflow-hidden shrink-0">
                  <img
                    src={entry.image}
                    alt={entry.title}
                    title={`${entry.title} - 成果转化`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="min-w-0">
                  <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-calendar-line"></i>
                    {entry.date}
                  </span>
                  <h3 className="mt-1 font-heading font-semibold text-sm md:text-base text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors">
                    {entry.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}